import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, MapPin, Search, X, Users, Building2 } from 'lucide-react';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { TableRoot, Table, THead, TR, TH, TBody, TD } from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { Region, Controller } from '../../lib/sdk/index';
import { toast } from 'react-toastify';
import { formatDate, getDateField, safeParseNumber } from '../../lib/utils/formatting';
import { useRegions, useControllers, useCreateRegion, useUpdateRegion, useDeleteRegion } from '../../hooks/useQueries';
import { getErrorMessage } from '../../lib/utils/errorMessage';

const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedValue(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

const ManageRegions: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 350);
  const limit = 50;
  
  // Use React Query hooks with pagination
  const { data: regionsResponse, isLoading: regionsLoading, refetch: refetchRegions } = useRegions({ 
    page, 
    limit, 
    search: debouncedSearchTerm || undefined 
  });
  const { data: controllersResponse = [] } = useControllers();
  
  const regions = Array.isArray(regionsResponse?.data) ? regionsResponse.data : [];
  const pagination = regionsResponse?.pagination;
  const controllers = Array.isArray(controllersResponse?.data) ? controllersResponse.data : (Array.isArray(controllersResponse) ? controllersResponse : []);
  const loading = regionsLoading;
  
  // Reset to page 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);
  
  const [showForm, setShowForm] = useState(false);
  const [editingRegion, setEditingRegion] = useState<Region | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteRegionId, setDeleteRegionId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    controllerId: '',
    allowedOffices: ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [controllerSearchTerm, setControllerSearchTerm] = useState('');
  const [showControllerDropdown, setShowControllerDropdown] = useState(false);

  // Mutations
  const createRegionMutation = useCreateRegion({
    onSuccess: () => {
      toast.success('Region created successfully!');
      setShowForm(false);
      setFormData({ name: '', controllerId: '', allowedOffices: '' });
      setErrors({});
      refetchRegions();
    },
    onError: (error: any) => {
      console.error('Error creating region:', error);
      toast.error(getErrorMessage(error, 'Failed to create region'));
    }
  });

  const updateRegionMutation = useUpdateRegion({
    onSuccess: () => {
      toast.success('Region updated successfully!');
      setShowForm(false);
      setEditingRegion(null);
      setFormData({ name: '', controllerId: '', allowedOffices: '' });
      setErrors({});
      refetchRegions();
    },
    onError: (error: any) => {
      console.error('Error updating region:', error);
      toast.error(getErrorMessage(error, 'Failed to update region'));
    }
  });

  const deleteRegionMutation = useDeleteRegion({
    onSuccess: () => {
      toast.success('Region deleted successfully!');
      setShowDeleteConfirm(false);
      setDeleteRegionId(null);
      refetchRegions();
    },
    onError: (error: any) => {
      console.error('Error deleting region:', error);
      toast.error(getErrorMessage(error, 'Failed to delete region'));
      setShowDeleteConfirm(false);
      setDeleteRegionId(null);
    }
  });

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showControllerDropdown) {
        setShowControllerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showControllerDropdown]);


  // Validation functions
  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Region name is required';
    }

    if (!formData.controllerId.trim()) {
      newErrors.controllerId = 'Controller is required';
    }

    if (!formData.allowedOffices.trim()) {
      newErrors.allowedOffices = 'Number of allowed offices is required';
    } else {
      const num = parseInt(formData.allowedOffices);
      if (isNaN(num) || num < 1 || num > 100) {
        newErrors.allowedOffices = 'Allowed offices must be between 1 and 100';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    try {
      // Convert allowedOffices to number for API
      const submitData = {
        name: formData.name.trim(),
        controllerId: formData.controllerId,
        allowedOffices: parseInt(formData.allowedOffices)
      };

      if (editingRegion) {
        updateRegionMutation.mutate({
          id: editingRegion._id,
          data: submitData
        });
      } else {
        createRegionMutation.mutate(submitData);
      }
    } catch (error: any) {
      // Display the specific error message from the backend
      toast.error(getErrorMessage(error, 'An unexpected error occurred'));
    }
  };

  const handleEdit = (region: Region) => {
    setEditingRegion(region);
    const controllerId = typeof region.controllerId === 'string' ? region.controllerId : region.controllerId._id;
    const controllerName = typeof region.controllerId === 'object' ? region.controllerId.name : '';
    
    setFormData({
      name: region.name,
      controllerId: controllerId,
      allowedOffices: region.allowedOffices.toString()
    });
    
    // Set the controller search term to show the selected controller name
    setControllerSearchTerm(controllerName);
    
    setShowForm(true);
    setErrors({});
  };

  const handleDelete = (id: string) => {
    setDeleteRegionId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (!deleteRegionId) return;
    deleteRegionMutation.mutate(deleteRegionId);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteRegionId(null);
  };

  const resetForm = () => {
    setFormData({ name: '', controllerId: '', allowedOffices: '' });
    setEditingRegion(null);
    setShowForm(false);
    setErrors({});
    setControllerSearchTerm('');
    setShowControllerDropdown(false);
  };


  const filteredControllers = controllers.filter(controller =>
    controller.name.toLowerCase().includes(controllerSearchTerm.toLowerCase()) ||
    controller.cnic.includes(controllerSearchTerm)
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Manage Regions</h1>
          <p className="text-gray-600 dark:text-gray-400">Add, edit, and delete regions</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Region
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <MapPin className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Regions</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{pagination?.total || regions.length}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <Building2 className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Offices Allowed</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {regions.reduce((total, region) => total + region.allowedOffices, 0)}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <Users className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Current Offices</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {regions.reduce((total, region) => total + (region.currentOffices || 0), 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <Input
            type="text"
            placeholder="Search regions by name or controller..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="card">
        {regions.length === 0 ? (
          <div className="text-center py-12">
            <MapPin className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No regions found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Get started by adding your first region.
            </p>
          </div>
        ) : (
          <>
            <TableRoot>
              <Table>
                <THead>
                  <TR>
                    <TH>Region ID</TH>
                    <TH>Name</TH>
                    <TH>Controller</TH>
                    <TH>Allowed Offices</TH>
                    <TH>Current Offices</TH>
                    <TH>Office Range</TH>
                    <TH>Created Date</TH>
                    <TH>Updated Date</TH>
                    <TH>Actions</TH>
                  </TR>
                </THead>
                <TBody>
                  {regions.map((region) => (
                    <TR key={region._id}>
                      <TD className="font-medium">{region.regionId}</TD>
                      <TD>{region.name}</TD>
                      <TD>
                        {region.controllerId && typeof region.controllerId === 'object' && 'name' in region.controllerId 
                          ? (region.controllerId as any).name 
                          : 'Unassigned'
                        }
                      </TD>
                      <TD>{safeParseNumber(region.allowedOffices, 0)}</TD>
                      <TD>{safeParseNumber(region.currentOffices, 0)}</TD>
                      <TD>
                        {region.officeRange ? 
                          `${safeParseNumber(region.officeRange.start)}-${safeParseNumber(region.officeRange.end)}` : 
                          'N/A'
                        }
                      </TD>
                      <TD>{formatDate(region.createdAt || region.created_at)}</TD>
                      <TD>{formatDate(region.updatedAt || region.updated_at)}</TD>
                      <TD>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(region)}
                            className="flex items-center gap-1"
                          >
                            <Edit className="h-4 w-4" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(region._id)}
                            className="flex items-center gap-1 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </Button>
                        </div>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableRoot>
            
            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} regions
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={pagination.page === 1 || loading}
                  >
                    Previous
                  </Button>
                  <span className="flex items-center px-4 text-sm text-gray-700 dark:text-gray-300">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                    disabled={pagination.page === pagination.totalPages || loading}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {editingRegion ? 'Edit Region' : 'Add Region'}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={resetForm}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="form-label">Region Name</label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Enter region name"
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
              
              <div>
                <label className="form-label">Controller</label>
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Search controllers..."
                    value={controllerSearchTerm}
                    onChange={(e) => {
                      setControllerSearchTerm(e.target.value);
                      setShowControllerDropdown(true);
                    }}
                    onFocus={() => setShowControllerDropdown(true)}
                    className="mb-2"
                  />
                  
                  {/* Simple dropdown for testing */}
                  {showControllerDropdown && (
                    <div className="absolute z-10 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {controllers
                        .filter(controller =>
                          controller.name.toLowerCase().includes(controllerSearchTerm.toLowerCase()) ||
                          controller.cnic.includes(controllerSearchTerm)
                        )
                        .map((controller) => (
                          <div
                            key={controller._id}
                            className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-b-0"
                            onMouseDown={(e) => {
                              e.preventDefault(); // Prevent input blur
                              e.stopPropagation(); // Prevent event bubbling
                              
                              
                              // Update state using the same approach as the test button
                              setFormData(prev => {
                                const newData = { ...prev, controllerId: controller._id };
                                return newData;
                              });
                              
                              setControllerSearchTerm(controller.name);
                              setShowControllerDropdown(false);
                            }}
                          >
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {controller.name} - {controller.cnic}
                            </div>
                          </div>
                        ))}
                      {controllers.filter(controller =>
                        controller.name.toLowerCase().includes(controllerSearchTerm.toLowerCase()) ||
                        controller.cnic.includes(controllerSearchTerm)
                      ).length === 0 && (
                        <div className="px-3 py-2 text-gray-500 dark:text-gray-400">
                          No controllers found
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Current selection display */}
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Current Controller: <span className="font-medium text-gray-900 dark:text-gray-100">
                    {controllers.find(c => c._id === formData.controllerId)?.name || 'None'}
                  </span>
                </div>
                
                {errors.controllerId && <p className="text-red-500 text-xs mt-1">{errors.controllerId}</p>}
              </div>
              
              <div>
                <label className="form-label">Allowed Offices</label>
                <Input
                  type="number"
                  value={formData.allowedOffices}
                  onChange={(e) => setFormData({ ...formData, allowedOffices: e.target.value })}
                  required
                  placeholder="Enter number of allowed offices"
                  min="1"
                  max="100"
                  className={errors.allowedOffices ? 'border-red-500' : ''}
                />
                <p className="text-xs text-gray-500 mt-1">Number of offices this region can manage</p>
                {errors.allowedOffices && <p className="text-red-500 text-xs mt-1">{errors.allowedOffices}</p>}
              </div>
              
              <div className="flex space-x-3 pt-4">
                <Button type="submit" className="flex-1">
                  {editingRegion ? 'Update Region' : 'Add Region'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm} className="flex-1">
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Confirm Deletion</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to delete this region? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={cancelDelete} className="flex-1">
                Cancel
              </Button>
              <Button variant="error" onClick={confirmDelete} className="flex-1">
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageRegions;
